import type { SessionSaccoUser } from "../types/session";
import { userAccessLevels, userRoles } from "../utilities/constanst";

/** Capability keys — used for route guards and navigation visibility. */
export const Permission = {
  DASHBOARD: "page:dashboard",
  PROFILE: "page:profile",
  FARECHART: "page:farechart",
  TRANSACTIONS: "page:transactions",
  MATATU: "page:matatu",
  PARCEL_SERVICE: "page:parcel-service",
  CREW_MANAGER: "page:crew-manager",
  WITHDRAW: "page:withdraw",
  SAVINGS: "page:savings",
  SAVINGS_WITHDRAWAL: "page:savings.withdrawal",
  LOANS: "page:loans",
  NOTIFICATIONS: "page:notifications",
  OPERATORS: "page:operators",
  SACCO_REGISTRATION: "page:sacco.registration",
  SACCO_TEAM: "page:sacco.team",
  SACCO_FINANCES: "page:sacco.finances",
  SACCO_WALLET_TRANSACTIONS: "page:sacco.wallet-transactions",
  SACCO_STAGE_COLLECTIONS: "page:sacco.stage-collections",
  SACCO_STALL_MANAGEMENT: "page:sacco.stall-management",
  SACCO_PROFILE: "page:sacco.profile",
  SACCO_COLLECTIONS: "page:sacco.collections",
  DASHBOARD_ACCOUNTING: "widget:dashboard.accounting",
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

export type SaccoUserLike = SessionSaccoUser | null | undefined;

export type AccessResult = {
  level: string;
  allowedPage: boolean;
};

const ALL_PAGE_PERMISSIONS: Permission[] = Object.values(Permission);

/** Sacco Management menu + guarded /sacco/* routes (except public onboarding). */
const SACCO_MANAGEMENT_PERMISSIONS: readonly Permission[] = [
  Permission.SACCO_REGISTRATION,
  Permission.SACCO_TEAM,
  Permission.SACCO_FINANCES,
  Permission.SACCO_WALLET_TRANSACTIONS,
  Permission.SACCO_STAGE_COLLECTIONS,
  Permission.SACCO_STALL_MANAGEMENT,
  Permission.SACCO_COLLECTIONS,
];

const allPagePermissionsExcept = (
  ...exclude: Permission[]
): Permission[] =>
  ALL_PAGE_PERMISSIONS.filter((p) => !exclude.includes(p));

/** Primary access level per role (first level when a role maps to several). */
const ROLE_LEVEL: Record<string, string> = {
  [userRoles.Admin]: userAccessLevels.ADMIN,
  [userRoles.MainBrandAmbassador]: userAccessLevels.WITHDRAWAL,
  [userRoles.BrandAmbassador]: userAccessLevels.STAFF,
  [userRoles.SaccoSuperAdmin]: userAccessLevels.SACCO_ADMIN,
  [userRoles.SaccoAdmin]: userAccessLevels.SACCO,
  [userRoles.SaccoStaff]: userAccessLevels.SACCO_STAFF,
  [userRoles.SaccoCollector]: userAccessLevels.SACCO_COLLECTOR,
  [userRoles.SaccoParcelUser]: userAccessLevels.PARCEL_BASIC,
  [userRoles.SaccoParcelAdmin]: userAccessLevels.PARCEL_ADMIN,
  [userRoles.StageManager]: userAccessLevels.STAGE_MANAGER,
  [userRoles.StageUser]: userAccessLevels.STAGE_USER,
};

/**
 * Permissions granted to each access level.
 * Keep in sync with menu visibility via can() / getNavVisibility().
 */
const LEVEL_PERMISSIONS: Record<string, readonly Permission[]> = {
  [userAccessLevels.ADMIN]: ALL_PAGE_PERMISSIONS,
  [userAccessLevels.WITHDRAWAL]: allPagePermissionsExcept(
    Permission.LOANS,
    ...SACCO_MANAGEMENT_PERMISSIONS.filter(
      (p) =>
        p !== Permission.SACCO_STAGE_COLLECTIONS &&
        p !== Permission.SACCO_STALL_MANAGEMENT &&
        p !== Permission.SACCO_COLLECTIONS,
    ),
  ),
  [userAccessLevels.STAFF]: allPagePermissionsExcept(
    Permission.WITHDRAW,
    Permission.LOANS,
    Permission.SAVINGS,
    Permission.SACCO_PROFILE,
    ...SACCO_MANAGEMENT_PERMISSIONS.filter(
      (p) =>
        p !== Permission.SACCO_STAGE_COLLECTIONS &&
        p !== Permission.SACCO_STALL_MANAGEMENT &&
        p !== Permission.SACCO_COLLECTIONS,
    ),
  ),
  [userAccessLevels.PARCEL_BASIC]: [
    Permission.PROFILE,
    Permission.PARCEL_SERVICE,
  ],
  [userAccessLevels.PARCEL_ADMIN]: [
    Permission.DASHBOARD,
    Permission.PROFILE,
    Permission.PARCEL_SERVICE,
    Permission.SACCO_WALLET_TRANSACTIONS,
    Permission.SACCO_STAGE_COLLECTIONS,
    Permission.SACCO_STALL_MANAGEMENT,
    Permission.SACCO_COLLECTIONS,
  ],
  [userAccessLevels.SACCO_ADMIN]: [
    Permission.PROFILE,
    Permission.DASHBOARD,
    Permission.OPERATORS,
    Permission.PARCEL_SERVICE,
    Permission.SACCO_WALLET_TRANSACTIONS,
    Permission.SACCO_STAGE_COLLECTIONS,
    Permission.SACCO_STALL_MANAGEMENT,
    Permission.SACCO_COLLECTIONS,
    Permission.SACCO_PROFILE,
    Permission.DASHBOARD_ACCOUNTING,
  ],
  [userAccessLevels.SACCO]: [
    Permission.PROFILE,
    Permission.DASHBOARD,
    Permission.OPERATORS,
    Permission.PARCEL_SERVICE,
    Permission.SACCO_WALLET_TRANSACTIONS,
    Permission.SACCO_STAGE_COLLECTIONS,
    Permission.SACCO_STALL_MANAGEMENT,
    Permission.SACCO_COLLECTIONS,
    Permission.DASHBOARD_ACCOUNTING,
  ],
  [userAccessLevels.SACCO_STAFF]: [Permission.PROFILE, Permission.OPERATORS],
  [userAccessLevels.SACCO_COLLECTOR]: [
    Permission.PROFILE,
    Permission.SACCO_COLLECTIONS,
  ],
  [userAccessLevels.BASIC]: [Permission.PROFILE, Permission.OPERATORS],
  [userAccessLevels.STAGE_MANAGER]: [
    Permission.PROFILE,
    Permission.SACCO_STAGE_COLLECTIONS,
    Permission.SACCO_STALL_MANAGEMENT,
    Permission.SACCO_COLLECTIONS,
  ],
  [userAccessLevels.STAGE_USER]: [
    Permission.PROFILE,
    Permission.SACCO_STAGE_COLLECTIONS,
    Permission.SACCO_STALL_MANAGEMENT,
    Permission.SACCO_COLLECTIONS,
    Permission.PARCEL_SERVICE,
  ],
};

/** Routes that do not require an explicit Permission entry (e.g. public member onboarding). */
const UNGUARDED_PATH_PREFIXES = ["/sacco/account/register"];

const PATH_PERMISSION: Record<string, Permission> = {
  "/": Permission.DASHBOARD,
  "/profile": Permission.PROFILE,
  "/farechart": Permission.FARECHART,
  "/transactions": Permission.TRANSACTIONS,
  "/matatu": Permission.MATATU,
  "/parcel-service": Permission.PARCEL_SERVICE,
  "/crew-manager": Permission.CREW_MANAGER,
  "/withdraw": Permission.WITHDRAW,
  "/savings": Permission.SAVINGS,
  "/loans": Permission.LOANS,
  "/notifications": Permission.NOTIFICATIONS,
  "/operators": Permission.OPERATORS,
  "/sacco/register": Permission.SACCO_REGISTRATION,
  "/sacco/registration": Permission.SACCO_REGISTRATION,
  "/sacco/team": Permission.SACCO_TEAM,
  "/sacco/finances": Permission.SACCO_FINANCES,
  "/sacco/wallet-transactions": Permission.SACCO_WALLET_TRANSACTIONS,
  "/sacco/stage-collections": Permission.SACCO_STAGE_COLLECTIONS,
  "/sacco/stall-management": Permission.SACCO_STALL_MANAGEMENT,
  "/sacco/collections": Permission.SACCO_COLLECTIONS,
  "/sacco/profile": Permission.SACCO_PROFILE,
};

export function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  const path = pathname.split("?")[0].split("#")[0];
  if (path.length > 1 && path.endsWith("/")) {
    return path.slice(0, -1);
  }
  return path;
}

export function permissionForPath(pathname: string): Permission | null {
  const path = normalizePath(pathname);
  if (PATH_PERMISSION[path]) {
    return PATH_PERMISSION[path];
  }
  if (path.startsWith("/notifications/")) {
    return Permission.NOTIFICATIONS;
  }
  return null;
}

export function isUnguardedPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return UNGUARDED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function getAccessLevelForRole(role: unknown): string {
  if (typeof role !== "string" || !role) return "";
  return ROLE_LEVEL[role] ?? userAccessLevels.BASIC;
}

export function getAccessLevel(user: SaccoUserLike): string {
  return getAccessLevelForRole(user?.role);
}

export function can(user: SaccoUserLike, permission: Permission): boolean {
  const level = getAccessLevel(user);
  if (!level) return false;
  const grants = LEVEL_PERMISSIONS[level];
  return grants?.includes(permission) ?? false;
}

export function canAccessPath(user: SaccoUserLike, pathname: string): boolean {
  if (isUnguardedPath(pathname)) {
    return true;
  }

  const permission = permissionForPath(pathname);
  if (permission) {
    return can(user, permission);
  }

  const path = normalizePath(pathname);
  const level = getAccessLevel(user);

  if (path.startsWith("/sacco")) {
    if (level === userAccessLevels.ADMIN) {
      return true;
    }
    if (
      level === userAccessLevels.PARCEL_ADMIN &&
      (path.startsWith("/sacco/wallet-transactions") ||
        path.startsWith("/sacco/stage-collections"))
    ) {
      return true;
    }
    return false;
  }

  return false;
}

/** Route guard + legacy callers — { level, allowedPage }. */
export function resolveAccess(
  user: SaccoUserLike,
  pathname = "",
): AccessResult {
  const level = getAccessLevel(user);
  return {
    level,
    allowedPage: canAccessPath(user, pathname),
  };
}

export type NavVisibility = {
  showFullMatatuNav: boolean;
  isParcelAdminNav: boolean;
  isParcelBasicNav: boolean;
  showDashboardNav: boolean;
  showParcelServiceNav: boolean;
  showOperatorsNav: boolean;
  showSaccoMgmtDropdown: boolean;
  showSaccoRegistrationNav: boolean;
  showSaccoTeamNav: boolean;
  showSaccoFinancesNav: boolean;
  showWalletTransactionsNav: boolean;
  showStageCollectionsNav: boolean;
  showStallManagementNav: boolean;
  showSaccoCollectionsNav: boolean;
  showSaccoProfileNav: boolean;
  showWalletBalanceCard: boolean;
  showCrewWithdrawNav: boolean;
  showCrewSavingsNav: boolean;
  showCrewLoansNav: boolean;
  showDashboardAccountingTab: boolean;
};

/** Navigation visibility derived from the same permission grants as route access. */
export function getNavVisibility(user: SaccoUserLike): NavVisibility {
  const level = getAccessLevel(user);
  const isParcelBasicNav = level === userAccessLevels.PARCEL_BASIC;
  const isParcelAdminNav = level === userAccessLevels.PARCEL_ADMIN;
  const isParcelNavUser = isParcelBasicNav || isParcelAdminNav;

  const isRestrictedNavUser =
    level === userAccessLevels.SACCO ||
    level === userAccessLevels.SACCO_ADMIN ||
    level === userAccessLevels.SACCO_STAFF ||
    level === userAccessLevels.SACCO_COLLECTOR ||
    level === userAccessLevels.STAGE_MANAGER ||
    level === userAccessLevels.STAGE_USER;

  const isSaccoLevelUser =
    level === userAccessLevels.SACCO ||
    level === userAccessLevels.SACCO_ADMIN;

  const showFullMatatuNav =
    Boolean(level) &&
    level !== userAccessLevels.BASIC &&
    !isParcelNavUser &&
    !isRestrictedNavUser;

  const showWalletTransactionsNav = can(
    user,
    Permission.SACCO_WALLET_TRANSACTIONS,
  );
  const showStageCollectionsNav = can(
    user,
    Permission.SACCO_STAGE_COLLECTIONS,
  );
  const showStallManagementNav = can(
    user,
    Permission.SACCO_STALL_MANAGEMENT,
  );
  const showSaccoCollectionsNav = can(user, Permission.SACCO_COLLECTIONS);
  const showSaccoRegistrationNav = can(user, Permission.SACCO_REGISTRATION);
  const showSaccoTeamNav = can(user, Permission.SACCO_TEAM);
  const showSaccoFinancesNav = can(user, Permission.SACCO_FINANCES);
  const showStandardSaccoMgmt =
    showSaccoRegistrationNav || showSaccoTeamNav || showSaccoFinancesNav;

  return {
    showFullMatatuNav,
    isParcelAdminNav,
    isParcelBasicNav,
    showDashboardNav: can(user, Permission.DASHBOARD),
    showParcelServiceNav:
      isRestrictedNavUser && can(user, Permission.PARCEL_SERVICE),
    showOperatorsNav: can(user, Permission.OPERATORS),
    showSaccoMgmtDropdown:
      !isParcelAdminNav &&
      (showWalletTransactionsNav ||
        showStandardSaccoMgmt ||
        showStageCollectionsNav ||
        showStallManagementNav ||
        isSaccoLevelUser),
    showWalletBalanceCard:
      !isParcelAdminNav && level !== userAccessLevels.STAFF,
    showSaccoRegistrationNav,
    showSaccoTeamNav,
    showSaccoFinancesNav,
    showWalletTransactionsNav,
    showStageCollectionsNav,
    showStallManagementNav,
    showSaccoCollectionsNav,
    showSaccoProfileNav: can(user, Permission.SACCO_PROFILE),
    showCrewWithdrawNav: can(user, Permission.WITHDRAW),
    showCrewSavingsNav: can(user, Permission.SAVINGS),
    showCrewLoansNav: can(user, Permission.LOANS),
    showDashboardAccountingTab: can(user, Permission.DASHBOARD_ACCOUNTING),
  };
}
